import type { CallInfo } from '@compass/client/types/call';
import { useQuery } from '@tanstack/react-query';

import { callService } from '@/services/api';

interface UseMyCallHistoryOptions {
  /** Filter by call type (e.g., 'external_call') */
  type?: string;
  /** Temporary Client-side filter by case_state (e.g., 'created') */
  caseState?: string;
}

/**
 * Hook to fetch the current user's recent calls (all statuses).
 * Polls every 30 seconds to keep the call history up to date.
 *
 * Unlike useActiveCall which only fetches in_progress calls,
 * this hook returns all recent calls for the call history panel.
 */
export function useMyCallHistory(enabled = true, options?: UseMyCallHistoryOptions) {
  const query = useQuery({
    queryKey: ['myCallHistory', options?.type, options?.caseState],
    queryFn: async () => {
      const response = await callService.getMyUserCalls({
        limit: 40,
        ...(options?.type ? { type: options.type } : {}),
      });
      //Temporary client side filtration
      const calls = (response.calls ?? []) as CallInfo[];
      if (options?.caseState) {
        return calls.filter(c => c.case_state === options.caseState);
      }
      return calls;
    },
    enabled,
    refetchInterval: 30000,
    staleTime: 30000,
  });

  return {
    calls: query.data ?? [],
    isLoading: query.isLoading,
  };
}
